// DESCRIPTION: Generic renderer for developer-injected attendee fields registered via the
// DESCRIPTION: `nemtly_booking.booking.attendee_fields` filter. Built-in fields stay bespoke JSX.

import React from 'react';

/**
 * DynamicField — renders a single injected attendee field.
 *
 * @param {Object}   props
 * @param {Object}   props.field            Field descriptor: { key, type, label, required, options, placeholder }.
 * @param {*}        props.value            Current field value.
 * @param {string}   [props.error]          Validation error message, if any.
 * @param {Function} props.onChange         Called with ( key, value ) when the field changes.
 * @param {boolean}  [props.disabled=false] Disable the field input.
 *
 * @return {JSX.Element} The rendered field.
 */
const DynamicField = ( {
	field,
	value,
	error,
	onChange,
	disabled = false,
} ) => {
	const {
		key,
		type = 'text',
		label,
		required,
		options = [],
		placeholder,
	} = field;
	const fieldId = `nb-field-${ key }`;
	const update = ( newValue ) => onChange( key, newValue );

	if ( type === 'checkbox' ) {
		return (
			<button
				type="button"
				className="nb-checkbox-row"
				onClick={ () => update( ! value ) }
				aria-pressed={ !! value }
				disabled={ disabled }
			>
				<span
					className={ `nb-checkbox${
						value ? ' nb-checkbox--checked' : ''
					}` }
					aria-hidden="true"
				/>
				<span className="nb-checkbox-row__text">
					{ label }
					{ required && <span className="nb-field__req">*</span> }
				</span>
			</button>
		);
	}

	return (
		<div className="nb-field">
			<label className="nb-field__label" htmlFor={ fieldId }>
				{ label }
				{ required && <span className="nb-field__req">*</span> }
			</label>

			{ type === 'textarea' && (
				<textarea
					id={ fieldId }
					className={ `nb-input${ error ? ' nb-input--error' : '' }` }
					rows="3"
					value={ value || '' }
					onChange={ ( e ) => update( e.target.value ) }
					placeholder={ placeholder }
					disabled={ disabled }
					aria-invalid={ !! error }
				/>
			) }

			{ type === 'select' && (
				<select
					id={ fieldId }
					className={ `nb-input${ error ? ' nb-input--error' : '' }` }
					value={ value || '' }
					onChange={ ( e ) => update( e.target.value ) }
					disabled={ disabled }
					aria-invalid={ !! error }
				>
					{ options.map( ( option ) => (
						<option key={ option.value } value={ option.value }>
							{ option.label }
						</option>
					) ) }
				</select>
			) }

			{ ( type === 'text' ||
				type === 'email' ||
				type === 'tel' ||
				type === 'number' ) && (
				<input
					id={ fieldId }
					type={ type }
					className={ `nb-input${ error ? ' nb-input--error' : '' }` }
					value={ value || '' }
					onChange={ ( e ) => update( e.target.value ) }
					placeholder={ placeholder }
					disabled={ disabled }
					aria-invalid={ !! error }
				/>
			) }

			{ error && <span className="nb-field__error">{ error }</span> }
		</div>
	);
};

export default DynamicField;
